import React, { useEffect, useState } from "react";
import {
  Grid,
  Box,
  Paper,
  Divider,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import * as actions from "../Releases/actions";
import * as commonActions from "../Common/actions";
import moment from "moment";
import ConfirmModal from "../Common/ConfirmModal";
import CustomAddModal from "../Common/CustomAddModal";
import ImagePreviewModal from "../Common/ImagePreviewModal";
import { toastSuccess, toastError, copyToClipboard } from "../../utils";
import SelectCMP from "react-select";
import darkSelectStyles from "../../theme/reactSelectStyles";
import ActionButton from "../../components/ActionButton";
import StatusBadge from "../../components/StatusBadge";
import CopyableField from "../../components/CopyableField";
import { ChevronDown, Copy, ClipboardCheck, ExternalLink, Download } from "../../components/Icons";

function ViewVideoReleases(props) {
  const params = useParams();
  const navigate = useNavigate();

  const [release, setRelease] = useState({});
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openReject, setOpenReject] = useState(false);
  const [reasonText, setReasonText] = useState("");
  const [rejectOptions, setRejectOptions] = useState([]);
  const [recipeFilePreview, setRecipeFilePreview] = useState(null);
  const [updatedPoster, setUpdatedPoster] = useState("");
  const [openImagePreview, setOpenImagePreview] = useState(false);

  const onFileHandler = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1572864) {
        return toastError("File more than 1.5 MB is not allowed.");
      }
      props.setLoader(true);
      commonActions
        .videoUpload(file)
        .then((res) => {
          props.setLoader(false);
          setUpdatedPoster(res.body.filename);
          previewsFile(file);
          toastSuccess("Thumbnail uploaded successfully.");
        })
        .catch((err) => {
          props.setLoader(false);
          toastError(err.message || "Something went wrong");
        });
    }
  };

  const onDelete = (item) => {
    props.setLoader(true);
    actions
      .deleteVThumnailFromServer({ fileLink: item })
      .then((res) => {
        window.location.reload();
        toastSuccess(res.message);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || "Failed to delete thumbnail");
      });
  };

  const onSaveUpdatedArtwork = () => {
    actions
      .updateVThumbnailAdmin({
        video_id: params.id,
        thumbnail:
          `https://backend.fronicmedia.com/${updatedPoster}` || "",
      })
      .then((res) => {
        setRecipeFilePreview(null);
        onGetRelease();
        toastSuccess(res.message || "Updated successfully");
      })
      .catch((err) => {
        toastError(err.message);
      });
  };

  const previewsFile = (file) => {
    const reader = new FileReader();
    reader.addEventListener(
      "load",
      function () {
        setRecipeFilePreview(reader.result);
      },
      false
    );
    if (file) {
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    onGetRelease();
    onGetRejectOptions();
  }, []);

  const onGetRejectOptions = () => {
    actions
      .rejectReasonsAdmin()
      .then((res) => {
        setRejectOptions(
          Array.isArray(res.body)
            ? res.body.map((item) => ({
                label: item.reason,
                value: item.reason,
              }))
            : []
        );
      })
      .catch((err) => {
        toastError(err?.message || "Failed to load reject reasons");
      });
  };

  const onGetRelease = () => {
    props.setLoader(true);
    actions
      .getVReleaseByIdAdmin({ video_id: params.id })
      .then((res) => {
        props.setLoader(false);
        setRelease(res.body);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || "Failed to load video release details");
      });
  };

  const onConfirmAcceptModal = () => {
    props.setLoader(true);
    actions
      .changeVideoReleaseStatusAdmin({
        status: "approved",
        video_id: selectedId,
      })
      .then((res) => {
        setOpenConfirm(false);
        onGetRelease();
        toastSuccess(res.message);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || "Failed to approve video release");
      });
  };

  const onClickAccept = (id) => {
    setSelectedId(id);
    setOpenConfirm(true);
  };

  const onSubmitReject = (id) => {
    props.setLoader(true);
    actions
      .changeVideoReleaseStatusAdmin({
        video_id: id || release.video_id,
        status: "rejected",
        reason: reasonText,
      })
      .then((res) => {
        setOpenReject(false);
        onGetRelease();
        toastSuccess(res.message);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || "Failed to reject video release");
      });
  };

  const onDeleteRejectedRelease = (video_id) => {
    props.setLoader(true);
    actions
      .deleteVideoReleaseAdmin({ video_id })
      .then((res) => {
        toastSuccess(res.message);
        props.setLoader(false);
        return navigate("/video_releases/history");
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || "Failed to delete video release");
      });
  };

  const keyPressedReason = (e) => {
    if (e.key === "Enter" && e.shiftKey) {
      return;
    } else if (e.key === "Enter") {
      e.preventDefault();
      onSubmitReject();
    } else return;
  };

  const onTakeDownRelease = (video_id) => {
    props.setLoader(true);
    actions
      .changeVideoReleaseStatusAdmin({ video_id, status: "takendown" })
      .then((res) => {
        onGetRelease();
        toastSuccess(res.message);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || "Failed to takedown video release");
      });
  };

  const {
    video_id = "",
    release_id = "",
    release_title = "",
    audio_title = "",
    name = "",
    email = "",
    thumbnail = "",
    download_link = "",
    release_date = "",
    video_isrc = "",
    video_upc = "",
    actor = "",
    actress = "",
    status = "",
  } = release;

  const hasThumbnail = recipeFilePreview || thumbnail;

  return (
    <Grid container className="view-release-container" spacing={3}>
      {/* ==================== TOP HEADER ==================== */}
      <Grid item xs={12}>
        <Box className="view-release-header">
          <Typography variant="h5" className="view-release-header__title">
            {release_title || "Video Release Details"}
          </Typography>
          <Box className="view-release-header__meta">
            {status && <StatusBadge status={status} />}
            <Typography
              variant="body2"
              className="view-release-header__id"
            >
              FM-VR-{video_id}
            </Typography>
          </Box>
        </Box>
      </Grid>

      {/* ==================== LEFT SIDEBAR ==================== */}
      <Grid item xs={12} md={3}>
        <Box className="view-release-sidebar">
          {/* Thumbnail */}
          <div className="sidebar-artwork">
            <div
              className={`image-div ${hasThumbnail ? "image-preview-clickable" : ""}`}
              onClick={() => hasThumbnail && setOpenImagePreview(true)}
              title={hasThumbnail ? "Click to preview" : ""}
            >
              {hasThumbnail ? (
                <img
                  src={recipeFilePreview || thumbnail}
                  alt="Video Thumbnail"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
              ) : null}
              <div
                className="sidebar-artwork__no-image"
                style={hasThumbnail ? { display: "none" } : {}}
              >
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#64748B"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span>No image available</span>
              </div>
            </div>

            {/* Thumbnail action buttons */}
            <Accordion className="sidebar-poster-actions" disableGutters>
              <AccordionSummary expandIcon={<ChevronDown size={16} />}>
                <Typography variant="subtitle2">
                  Thumbnail Actions
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="sidebar-poster-actions__content">
                {hasThumbnail && (
                  <a
                    href={recipeFilePreview || thumbnail}
                    download="thumbnail"
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: "none", display: "block" }}
                  >
                    <ActionButton variant="settle">Download</ActionButton>
                  </a>
                )}
                {recipeFilePreview ? (
                  <ActionButton
                    variant="success"
                    onClick={onSaveUpdatedArtwork}
                  >
                    Save
                  </ActionButton>
                ) : (
                  <ActionButton variant="success" component="label">
                    <input
                      type="file"
                      accept="image/x-png,image/gif,image/jpeg"
                      onChange={onFileHandler}
                      hidden
                    />
                    Update Thumbnail
                  </ActionButton>
                )}
                {hasThumbnail && (
                  <ActionButton
                    variant="danger"
                    onClick={() => onDelete(thumbnail)}
                  >
                    Delete
                  </ActionButton>
                )}
              </AccordionDetails>
            </Accordion>
          </div>

          {/* User Info */}
          <Box mt={2} className="sidebar-user-info">
            <CopyableField label="User" value={name} />
            <CopyableField label="Email" value={email} />
          </Box>

          {/* Context-appropriate Actions */}
          {status === "in_review" && (
            <Box mt={2} className="sidebar-actions">
              <Typography
                variant="subtitle2"
                className="sidebar-actions__title"
              >
                Actions
              </Typography>
              <div className="sidebar-actions__buttons">
                <ActionButton
                  variant="success"
                  onClick={() => onClickAccept(video_id)}
                >
                  Accept
                </ActionButton>
                <ActionButton
                  variant="danger"
                  onClick={() => onSubmitReject(video_id)}
                >
                  Reject
                </ActionButton>
              </div>
            </Box>
          )}

          {status === "approved" && (
            <Box mt={2} className="sidebar-actions">
              <Typography
                variant="subtitle2"
                className="sidebar-actions__title"
              >
                Actions
              </Typography>
              <div className="sidebar-actions__buttons">
                <ActionButton
                  variant="warning"
                  onClick={() => onTakeDownRelease(video_id)}
                >
                  Take Down
                </ActionButton>
                <ActionButton
                  variant="danger"
                  onClick={() => onSubmitReject(video_id)}
                >
                  Reject
                </ActionButton>
              </div>
            </Box>
          )}

          {status === "rejected" && (
            <Box mt={2} className="sidebar-actions">
              <Typography
                variant="subtitle2"
                className="sidebar-actions__title"
              >
                Actions
              </Typography>
              <div className="sidebar-actions__buttons">
                <ActionButton
                  variant="danger"
                  onClick={() => onDeleteRejectedRelease(video_id)}
                >
                  Delete Release
                </ActionButton>
              </div>
            </Box>
          )}

          {status === "takendown" && (
            <Box mt={2} className="sidebar-actions">
              <Typography
                variant="subtitle2"
                className="sidebar-actions__title"
              >
                Actions
              </Typography>
              <div className="sidebar-actions__buttons">
                <ActionButton
                  variant="danger"
                  onClick={() => onDeleteRejectedRelease(video_id)}
                >
                  Delete Release
                </ActionButton>
              </div>
            </Box>
          )}
        </Box>
      </Grid>

      {/* ==================== RIGHT CONTENT ==================== */}
      <Grid item xs={12} md={9}>
        <Box className="view-release-content">
          {/* Section 1: Release Information */}
          <Paper className="section-card">
            <Typography variant="h6" className="section-card__title">
              Release Information
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "var(--border)" }} />
            <CopyableField label="Release Title" value={release_title} />
            <CopyableField label="Audio Title" value={audio_title} />
            <CopyableField label="Release ID" value={release_id ? `FM-RL-${release_id}` : '-'} />
            <CopyableField
              label="Release Date"
              value={
                release_date
                  ? moment(Number(release_date)).format("DD MMM YYYY")
                  : "-"
              }
            />
          </Paper>

          {/* Section 2: Video Metadata */}
          <Paper className="section-card">
            <Typography variant="h6" className="section-card__title">
              Video Metadata
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "var(--border)" }} />
            <CopyableField label="ISRC" value={video_isrc} />
            <CopyableField label="UPC" value={video_upc} />
            <CopyableField label="Actor(s)" value={actor} />
            <CopyableField label="Actress" value={actress} />
          </Paper>

          {/* Section 3: Download */}
          <Paper className="section-card">
            <Typography variant="h6" className="section-card__title">
              Download
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "var(--border)" }} />
            <Box className="copyable-field">
              <span className="copyable-field__label">Download Link</span>
              <span className="copyable-field__value" style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                {download_link ? 'Available' : 'Not available'}
              </span>
              {download_link && (
                <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0, ml: 1 }}>
                  <Tooltip title="Copy link" arrow placement="top">
                    <IconButton size="small" onClick={() => { copyToClipboard(download_link); toastSuccess("Link copied"); }} sx={{ color: 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' } }}>
                      <Copy size={14} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Open in new tab" arrow placement="top">
                    <IconButton size="small" component="a" href={download_link} target="_blank" rel="noreferrer" sx={{ color: 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' } }}>
                      <ExternalLink size={14} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download" arrow placement="top">
                    <IconButton size="small" component="a" href={download_link} target="_blank" rel="noreferrer" download sx={{ color: 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' } }}>
                      <Download size={14} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Grid>

      {/* ==================== MODALS ==================== */}
      <ConfirmModal
        open={openConfirm}
        message="Are you sure you want to accept this release?"
        title="Confirmation"
        onSubmitConfirm={onConfirmAcceptModal}
        handleClose={() => setOpenConfirm(false)}
      />

      <CustomAddModal
        open={openReject}
        saveLabel="Send"
        title="Add Reason"
        handleClose={() => setOpenReject(false)}
        onSubmitConfirm={onSubmitReject}
      >
        <Grid container style={{ height: "330px", display: "block" }}>
          <Grid
            item
            xs={12}
            style={{ marginBottom: "20px", height: "40px" }}
          >
            <SelectCMP
              options={rejectOptions}
              placeholder="Select Reason"
              onChange={(e) => setReasonText(e.value)}
              styles={darkSelectStyles}
            />
          </Grid>
          <Grid item xs={12} style={{ height: "calc(100% - 60px)" }}>
            <textarea
              className="text-area-input"
              placeholder="Please add a reason"
              value={reasonText}
              onKeyPress={keyPressedReason}
              onChange={(e) => setReasonText(e.target.value)}
            />
          </Grid>
        </Grid>
      </CustomAddModal>

      <ImagePreviewModal
        open={openImagePreview}
        handleClose={() => setOpenImagePreview(false)}
        imageSrc={recipeFilePreview || thumbnail}
        title="Video Thumbnail"
      />
    </Grid>
  );
}

export default ViewVideoReleases;
